import { type Audience, SITE_LINKS } from './site-links'

/**
 * Every word on the public website, per audience, typed. Taken from the
 * 2026-09-29 "Homepage version 1 (launch)" reference.
 *
 * COPY RULES (from the reference, binding):
 * - Tenant side: no landlord language ("built by landlords", "20+ years"), no
 *   counts of work, privacy stated as a positive. Never describe what the
 *   landlord can see. Red is never used as emphasis.
 * - Landlord side: no "screening", no default rate or performance figure.
 * - Both: no em dashes; the landlord is named on any decision timing.
 * - The landlord timeline is one number set: tenant applies 5:00, AI analysis
 *   2:00, your review 1:00, lease signed 2:00, LMR collected 2:00 = 12:00.
 *   Change one and the hero copy, the clock and the track change together.
 */

export type TileColour = 'pink' | 'lav' | 'green'

export type SceneId =
	| 'renter-profile'
	| 'bank-statement'
	| 'device-morph'
	| 'fake-statement-lens'
	| 'approve-phone'
	| 'lease-lmr'

export interface FeatureCopy {
	id: SceneId
	heading: string
	body: string
	tile: TileColour
	/** Accessible description of what the animated tile shows. */
	ariaLabel: string
	/** The bank-statement row gives the visual more width than the text. */
	wide?: boolean
	/** The device-morph row gives the visual a little more width (.9fr / 1.1fr). */
	visualWide?: boolean
	/** The two tallest landlord tiles get a tighter row on phones. */
	compactOnPhone?: boolean
}

export interface TrackSegment {
	seconds: number
	/** The label painted inside the bar once the segment fills. */
	time: string
	name: string
	/** Optional break on phones (the reference splits one tenant label). */
	nameMobile?: readonly [string, string]
}

export interface TrackCopy {
	heading: readonly string[]
	unit: string
	totalSeconds: number
	segments: readonly TrackSegment[]
	/** Landlord only: the "You Approve" marker above the bar. */
	pin?: { label: string; atSeconds: number }
}

export interface HeroCopy {
	lines: string[]
	sub: string
	cta: string
	ctaHref: string
	secondary: string
}

export interface ClosingCopy {
	heading: string
	cta: string
	href: string
}

export interface PageMeta {
	title: string
	description: string
}

export const siteContent = {
	meta: {
		tenant: {
			title: 'TrueTenant. Five minutes. Nothing to upload.',
			description:
				'Apply from any device in five minutes, with nothing to upload. Your landlord can approve you in minutes.',
		},
		landlord: {
			title: 'TrueTenant for landlords. Lease in minutes, not days.',
			description:
				'From application to signed lease and last month’s rent in about 12 minutes. Verified income and rent history straight from the source.',
		},
		terms: {
			title: 'Terms of Service. TrueTenant',
			description: 'The TrueTenant Inc. Terms of Service.',
		},
		privacy: {
			title: 'Privacy Policy. TrueTenant',
			description:
				'How TrueTenant collects, uses, stores and protects personal information.',
		},
		cookies: {
			title: 'Cookie Policy. TrueTenant',
			description: 'How TrueTenant uses cookies and similar technologies.',
		},
	} satisfies Record<string, PageMeta>,

	nav: {
		howItWorks: 'How it Works',
		signIn: 'Sign In',
		getStarted: 'Get started',
		dashboard: 'Dashboard',
		startApplication: 'Start application',
		logOut: 'Log out',
		audience: {
			groupLabel: 'I am a',
			tenant: 'Tenant',
			landlord: 'Landlord',
		},
		menu: { open: 'Open menu', close: 'Close menu' },
		logoLabel: 'TrueTenant home',
	},

	hero: {
		tenant: {
			lines: ['Five Minutes.', 'Nothing To Upload.'],
			sub: 'Apply from any device. Your landlord can approve you in minutes.',
			cta: 'Get started',
			ctaHref: SITE_LINKS.getStarted.tenant,
			secondary: 'See how it works',
		},
		landlord: {
			lines: ['Lease In Minutes,', 'Not Days.'],
			sub: 'From application to signed lease and last month’s rent in about 12 minutes.',
			cta: 'Book a demo',
			ctaHref: SITE_LINKS.demo,
			secondary: 'See how it works',
		},
	} satisfies Record<Audience, HeroCopy>,

	track: {
		tenant: {
			heading: ['Five Steps.', 'Five Minutes.'],
			unit: 'Minutes, Start to Finish',
			totalSeconds: 300,
			segments: [
				{
					seconds: 50,
					time: '50 sec',
					name: 'Who You’re Applying With',
					nameMobile: ['Who You’re', 'Applying With'],
				},
				{ seconds: 60, time: '1 min', name: 'Your ID' },
				{ seconds: 60, time: '1 min', name: 'Rental History' },
				{ seconds: 60, time: '1 min', name: 'Employment History' },
				{ seconds: 70, time: '1 min 10 sec', name: 'Verify Your Income' },
			],
		},
		landlord: {
			heading: ['Application To Last Month’s Rent.'],
			unit: 'Total time, application to LMR',
			totalSeconds: 720,
			segments: [
				{ seconds: 300, time: '5 min', name: 'Tenant Applies' },
				{ seconds: 120, time: '2 min', name: 'AI Analysis' },
				{ seconds: 60, time: '1 min', name: 'Your Review' },
				{ seconds: 120, time: '2 min', name: 'Lease Signed' },
				{ seconds: 120, time: '2 min', name: 'LMR Collected' },
			],
			pin: { label: 'You Approve', atSeconds: 480 },
		},
	} satisfies Record<Audience, TrackCopy>,

	features: {
		tenant: [
			{
				id: 'renter-profile',
				heading: 'Built For Every Renter.',
				body: 'New to Canada, renting for the first time, or have low to minimal credit? Your landlord sees your real income and rent history, not just a credit score. Apply on your own or together with roommates, a partner or a guarantor.',
				tile: 'green',
				ariaLabel: 'How a renter’s profile looks to a landlord',
			},
			{
				id: 'bank-statement',
				heading: 'Who Else Has Your Bank Statements?',
				body: 'When you hand over your bank statements, they get passed from the agent to the property manager and then the landlord. You never know who else has a copy, or if it’s ever deleted. With TrueTenant, you verify your income through Flinks, your landlord only sees your income and rental transactions, and your data is deleted on a set schedule.',
				tile: 'lav',
				ariaLabel:
					'An emailed bank statement shows everything. Through TrueTenant only income and rent transactions are shared.',
				wide: true,
			},
			{
				id: 'device-morph',
				heading: 'Apply From Any Device.',
				body: 'Five quick steps: who you’re applying with, your ID, your rental and employment history, and verify your income. It only takes 5 minutes.',
				tile: 'pink',
				ariaLabel:
					'The TrueTenant application shown on a phone, a tablet and a laptop',
				visualWide: true,
			},
		],
		landlord: [
			{
				id: 'fake-statement-lens',
				heading: 'Fake Statements Look Real.',
				body: 'Anyone can now generate a pay stub or bank statement with AI in seconds. TrueTenant pulls your tenant’s information straight from the source.',
				tile: 'pink',
				ariaLabel:
					'A magnifier checks each line of a bank statement against the bank, then the verified income and rent card appears',
			},
			{
				id: 'approve-phone',
				heading: 'Get Back To Them In 2 Minutes.',
				body: 'Every applicant fills out a five-minute application. Get their result in 2 minutes. Act on good tenants right away, and catch a professional tenant before you hand over the keys.',
				tile: 'lav',
				ariaLabel:
					'A landlord gets a notification, picks one of four verified applications on their phone and approves it in 45 seconds',
				compactOnPhone: true,
			},
			{
				id: 'lease-lmr',
				heading: 'Lease And LMR In Minutes.',
				body: 'Build your own lease once. When you approve an application, the tenant’s details fill in instantly, so you can send it and collect last month’s rent right away.',
				tile: 'green',
				ariaLabel:
					'A custom lease: clauses switched on, tenant details filled in from the approved application, the tenant and landlord sign, and last month’s rent is collected',
				compactOnPhone: true,
			},
		],
	} satisfies Record<Audience, FeatureCopy[]>,

	closing: {
		tenant: {
			heading: 'Your Next Application Takes Five Minutes.',
			cta: 'Get started',
			href: SITE_LINKS.getStarted.tenant,
		},
		landlord: {
			heading: 'Start Approving Faster.',
			cta: 'Book a demo',
			href: SITE_LINKS.demo,
		},
	} satisfies Record<Audience, ClosingCopy>,

	footer: {
		terms: 'Terms of Service',
		privacy: 'Privacy Policy',
		/** Opens the cookie preferences dialog (reference of 2026-10-02). */
		privacyChoices: 'Your Privacy Choices',
		company: 'TrueTenant Inc.',
	},

	/**
	 * The dialog a "Get started" opens when the visitor has no apply link
	 * from a landlord (applications only start from one, 2026-10-14).
	 */
	getStarted: {
		dialogTitle: 'Application link needed',
		dialogIntro:
			'To continue, please ask your landlord to share their application link with you.',
		ok: 'Got it',
		close: 'Close',
	},

	/** The cookie banner and Your Privacy Choices dialog, on every page. */
	consent: {
		bannerLabel: 'Cookie consent',
		bannerText:
			'We use essential cookies to run TrueTenant. With your permission, we also use cookies for analytics and to improve your experience.',
		policyLink: 'Cookie Policy',
		acceptAll: 'Accept all',
		customize: 'Customize',
		dialogTitle: 'Your Privacy Choices',
		dialogIntro:
			'Essential cookies are always on. Everything else stays off until you turn it on.',
		save: 'Save preferences',
		close: 'Close',
		/** The banner's X: closing it keeps essential cookies only. */
		bannerClose: 'Close and keep essential cookies only',
		alwaysOn: 'always on',
		readPolicy: 'Read our Cookie Policy',
		saved: 'Your cookie choices are saved.',
		categories: {
			necessary: 'Essential',
			preferences: 'Functional',
			analytics: 'Analytics',
		},
	},

	legal: {
		switcher: {
			terms: 'Terms of Service',
			privacy: 'Privacy Policy',
			cookies: 'Cookie Policy',
		},
		onThisPage: 'On this page',
		backToTop: 'Back to top',
	},

	auth: {
		tagline: {
			tenant: { eyebrow: 'Where', lines: ['Renting', 'Feels Easy.'] },
			landlord: {
				eyebrow: 'Know Who',
				lines: ['You’re Handing', 'The Keys To.'],
			},
		},
		role: { tenant: 'Tenant.', landlord: 'Landlord.' },
		signup: {
			eyebrow: 'Create An Account As A',
			submit: 'Create Account',
			alt: 'Already have an account?',
			altLink: 'Log in',
			google: 'Continue with Google',
			consent:
				'By creating an account you agree to the Terms of Service and Privacy Policy.',
		},
		login: {
			eyebrow: 'Sign In As A',
			submit: 'Log In',
			forgot: 'Forgot Password?',
			alt: 'Don’t have an account?',
			altLink: 'Create an Account',
			google: 'Login with Google',
			consent:
				'By signing in you agree to the Terms of Service and Privacy Policy.',
		},
		forgot: {
			eyebrow: 'Forgot Your',
			heading: 'Password?',
			sub: 'We’ll send a verification code to your email address.',
			submit: 'Send Verification Code',
			alt: 'Remembered it?',
			altLink: 'Back to Sign In',
		},
		verify: {
			eyebrow: 'Enter Your',
			heading: 'Verification Code.',
			sub: 'We sent a verification code to',
			subGeneric: 'We sent a verification code to your email.',
			subTwoFactor: 'Enter the 6-digit code from your authenticator app.',
			resendReady: 'You can resend the code now.',
			codeLabel: 'Verification code',
			resendIn: 'Resend Code in:',
			resend: 'Resend Code',
			submit: 'Verify Code',
		},
		reset: {
			eyebrow: 'Reset Your',
			heading: 'Password.',
			sub: 'Your new password must be different from your previous passwords.',
			submit: 'Reset Password',
			mismatch: 'Passwords don’t match.',
		},
		setupOrg: {
			eyebrow: 'Set Up Your',
			heading: 'Organization.',
			sub: 'A few more details and your organization is ready.',
			signedInAs: 'Signed in as',
			submit: 'Create Organization',
		},
		changePassword: {
			eyebrow: 'Set Your',
			heading: 'Password.',
			sub: 'Choose a new password before you continue.',
			submit: 'Set Password',
		},
		/** Shown faintly inside a focused, empty field (the reference's placeholders). */
		hints: {
			firstName: 'Enter Your First Name',
			lastName: 'Enter Your Last Name',
			company: 'Enter Your Company Name',
			email: 'Enter Your Email',
			phone: 'Enter Your Phone Number',
			password: 'Enter Your Password',
			confirmPassword: 'Re-enter Your Password',
			newPassword: 'Enter New Password',
			confirmNewPassword: 'Confirm Password',
		},
		fields: {
			firstName: 'First Name',
			lastName: 'Last Name',
			company: 'Company Name',
			email: 'Email Address',
			phone: 'Phone Number',
			password: 'Password',
			confirmPassword: 'Confirm Password',
			newPassword: 'New Password',
			showPassword: 'Show password',
			hidePassword: 'Hide password',
			back: 'Back',
		},
		or: 'Or',
	},
} as const

export function copyFor<T>(table: Record<Audience, T>, audience: Audience): T {
	return table[audience]
}
